import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useMe, useRefreshMe, useSelectCompany } from '../../lib'
import { Empty, ErrorBox, Loading } from '../../app/ui'
import { PageBand } from '../../app/PageBand'
import type { Company, Suggestion } from './types'

function useSuggestions() {
  return useQuery({
    queryKey: ['accounts', 'suggest'],
    queryFn: () => api<{ rows: Suggestion[] }>('GET', '/api/companies/suggest'),
  })
}

function SuggestionList({ rows }: { rows: Suggestion[] }) {
  const qc = useQueryClient()
  const ask = useMutation({
    mutationFn: (id: string) => api('POST', `/api/companies/${id}/join-requests`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts', 'suggest'] }),
  })
  return (
    <ul className="stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {rows.map((s) => (
        <li key={s.id} className="row" data-testid="accounts-suggestion">
          <span>{s.name}</span>
          <span className="spacer" />
          {s.requested ? (
            <span className="muted" data-testid="accounts-join-requested">
              Asked. An admin will let you in.
            </span>
          ) : (
            <button className="button" disabled={ask.isPending} onClick={() => ask.mutate(s.id)} data-testid="accounts-ask-to-join">
              Ask to join
            </button>
          )}
        </li>
      ))}
      {ask.error ? <ErrorBox error={ask.error} /> : null}
    </ul>
  )
}

export function NewCompanyPage() {
  const navigate = useNavigate()
  // CHANGE-card: came here to apply for the ernd card (?then=card): go back to the application once the company exists
  const [params] = useSearchParams()
  const after = params.get('then') === 'card' ? '/card/apply' : '/'
  const refresh = useRefreshMe()
  const select = useSelectCompany()
  const sugg = useSuggestions()
  const [name, setName] = useState('')
  const [website, setWebsite] = useState('')
  const create = useMutation({
    mutationFn: () => api<Company>('POST', '/api/companies', { name, website: website || null }),
    onSuccess: async (c) => {
      select(c.id)
      await refresh()
      navigate(after)
    },
  })
  const rows = sugg.data?.rows ?? []

  return (
    <div className="page narrow stack" data-testid="accounts-new-company">
      <PageBand title="Create your company" />
      {rows.length > 0 ? (
        <section className="panel stack">
          <h2>Your team may already be here</h2>
          <SuggestionList rows={rows} />
        </section>
      ) : null}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault()
          create.mutate()
        }}
      >
        <label>
          Company name
          <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="accounts-company-name" />
        </label>
        <label>
          Website
          <input value={website} placeholder="acme.ai" onChange={(e) => setWebsite(e.target.value)} data-testid="accounts-company-website" />
        </label>
        <button className="button primary" type="submit" disabled={create.isPending} data-testid="accounts-create-company">
          Create company
        </button>
        {create.error ? <ErrorBox error={create.error} /> : null}
      </form>
    </div>
  )
}

export function JoinPage() {
  const { me } = useMe()
  const sugg = useSuggestions()
  if (sugg.isLoading) return <Loading />
  if (sugg.error) return <ErrorBox error={sugg.error} />
  const rows = sugg.data?.rows ?? []
  return (
    <div className="page narrow stack" data-testid="accounts-join">
      <PageBand title="Join your team" />
      {rows.length ? (
        <SuggestionList rows={rows} />
      ) : (
        <Empty>
          {(me?.companies.length ?? 0) > 0 ? 'No other team' : 'No team'} uses your email domain yet. Ask a teammate to invite you, or{' '}
          <Link to="/new">create your company</Link>.
        </Empty>
      )}
    </div>
  )
}
